// The course generator. A hole is a pure function of (course seed, hole index, brief). The brief is
// the one place the AI touches the world: it may propose a par, a theme, up to two hazards and a
// name, and the generator turns those constrained parameters into geometry deterministically. The
// AI never authors geometry, so a course is reproducible from a few bytes of JSON on either side of
// the wire. Coordinates: x runs across the hole (metres, centred on 0), z runs along it from the
// tee (z = 0) toward the cup (z = lengthM). y is up. Same frame in Three.js.

import { mulberry32, seedFrom } from './rng';

export type Surface = 'tee' | 'fairway' | 'green' | 'rough' | 'sand' | 'water' | 'oob';
export const SURFACES: Surface[] = ['tee', 'fairway', 'green', 'rough', 'sand', 'water', 'oob'];
export type Hazard = 'water' | 'sand' | 'trees';
export const HAZARDS: Hazard[] = ['water', 'sand', 'trees'];

export interface HoleBrief {
  name: string;
  par: 3 | 4 | 5;
  theme: string;
  hazards: Hazard[];
  quip: string;
  source: 'placeholder' | 'ai';
}

export interface CourseSpec {
  seed: number;
  holes: HoleBrief[];
}

export interface Hole {
  index: number;
  seed: number;
  brief: HoleBrief;
  lengthM: number;
  widthM: number;
  cell: number;
  nx: number;
  nz: number;
  tee: { x: number; z: number };
  cup: { x: number; z: number };
  greenRadius: number;
  /** nx * nz heights in metres, row-major by z then x. */
  heights: Float32Array;
  /** nx * nz surface ids, indexes into SURFACES. */
  surfaces: Uint8Array;
  centerline: { x: number; z: number }[];
}

export const CELL_M = 2;
export const WIDTH_M = 64;

const THEMES = ['coastal', 'parkland', 'heath', 'desert', 'mountain', 'meadow', 'moor', 'orchard'];
const NAMES = ['Long Walk', 'Cold Water', 'The Saddle', 'Widow Maker', 'Brief Mercy', 'Blind Faith', 'Low Sun', 'Last Light', 'The Kettle', 'Rook'];

/** A brief with no AI in it: deterministic from the seed, labelled as such. */
export function defaultBrief(index: number, courseSeed: number): HoleBrief {
  const r = mulberry32(seedFrom(courseSeed, index, 'brief'));
  const parRoll = r();
  const par: 3 | 4 | 5 = parRoll < 0.3 ? 3 : parRoll < 0.8 ? 4 : 5;
  const hazards: Hazard[] = [];
  if (r() < 0.6) hazards.push('sand');
  if (r() < 0.45) hazards.push('water');
  if (hazards.length < 2 && r() < 0.35) hazards.push('trees');
  return {
    name: NAMES[Math.floor(r() * NAMES.length)],
    par,
    theme: THEMES[Math.floor(r() * THEMES.length)],
    hazards: hazards.slice(0, 2),
    quip: 'No caddie on this one. Trust the yardage.',
    source: 'placeholder',
  };
}

/** Validate anything that claims to be a brief (an AI reply, a request body). Returns null if unusable. */
export function coerceBrief(raw: unknown, fallback: HoleBrief): HoleBrief {
  if (!raw || typeof raw !== 'object') return fallback;
  const o = raw as Record<string, unknown>;
  const par = Number(o.par);
  const hazards = Array.isArray(o.hazards)
    ? (o.hazards.filter((h): h is Hazard => typeof h === 'string' && (HAZARDS as string[]).includes(h)).slice(0, 2) as Hazard[])
    : fallback.hazards;
  return {
    name: typeof o.name === 'string' && o.name.trim() ? o.name.trim().slice(0, 40) : fallback.name,
    par: par === 3 || par === 4 || par === 5 ? (par as 3 | 4 | 5) : fallback.par,
    theme: typeof o.theme === 'string' && o.theme.trim() ? o.theme.trim().slice(0, 40) : fallback.theme,
    hazards,
    quip: typeof o.quip === 'string' ? o.quip.trim().slice(0, 200) : fallback.quip,
    source: fallback.source,
  };
}

function lengthFor(par: number, r: () => number): number {
  const base = par === 3 ? 120 + r() * 60 : par === 4 ? 280 + r() * 90 : 430 + r() * 90;
  return Math.round(base / CELL_M) * CELL_M;
}

export function generateHole(courseSeed: number, index: number, brief: HoleBrief): Hole {
  const seed = seedFrom(courseSeed, index, brief.par, brief.hazards.join(','));
  const r = mulberry32(seed);
  const lengthM = lengthFor(brief.par, r);
  const widthM = WIDTH_M;
  const cell = CELL_M;
  const nx = Math.round(widthM / cell) + 1;
  const nz = Math.round(lengthM / cell) + 1;

  const dogleg = brief.par === 3 ? 0 : (r() * 2 - 1) * 18;
  const centerX = (z: number) => dogleg * Math.sin((Math.PI * z) / lengthM);
  const centerline: { x: number; z: number }[] = [];
  for (let z = 0; z <= lengthM; z += 10) centerline.push({ x: centerX(z), z });

  const tee = { x: centerX(6), z: 6 };
  const cupZ = lengthM - 18 - r() * 8;
  const cup = { x: centerX(cupZ) + (r() * 2 - 1) * 6, z: cupZ };
  const greenRadius = 9 + r() * 4;

  const p1 = r() * 6.283, p2 = r() * 6.283, p3 = r() * 6.283, p4 = r() * 6.283;
  const fairwayHalf = brief.par === 3 ? 10 : 13;
  const treeSide = r() < 0.5 ? -1 : 1;

  // hazards, seeded
  type Ellipse = { x: number; z: number; rx: number; rz: number; rot: number };
  const bunkers: Ellipse[] = [];
  const ponds: Ellipse[] = [];
  if (brief.hazards.includes('sand')) {
    const n = 1 + Math.floor(r() * 2);
    for (let i = 0; i < n; i++) {
      const th = r() * Math.PI * 2;
      bunkers.push({ x: cup.x + Math.cos(th) * (greenRadius + 4), z: cup.z + Math.sin(th) * (greenRadius + 4), rx: 4 + r() * 2, rz: 3 + r() * 1.5, rot: r() * Math.PI });
    }
    if (brief.par > 3) {
      const zb = lengthM * (0.5 + r() * 0.15);
      bunkers.push({ x: centerX(zb) + (r() < 0.5 ? -1 : 1) * 10, z: zb, rx: 5, rz: 3.5, rot: r() * Math.PI });
    }
  }
  if (brief.hazards.includes('water')) {
    const zw = lengthM * (brief.par === 3 ? 0.45 : 0.62);
    ponds.push({ x: centerX(zw) + (r() < 0.5 ? -1 : 1) * 9, z: zw, rx: 13 + r() * 4, rz: 8 + r() * 3, rot: r() * 0.6 });
  }
  const inside = (e: Ellipse, x: number, z: number) => {
    const dx = x - e.x, dz = z - e.z;
    const c = Math.cos(e.rot), s = Math.sin(e.rot);
    const u = dx * c + dz * s, v = -dx * s + dz * c;
    return (u * u) / (e.rx * e.rx) + (v * v) / (e.rz * e.rz) <= 1;
  };

  const heights = new Float32Array(nx * nz);
  const surfaces = new Uint8Array(nx * nz);
  // hazard depressions are kept aside and added after the green is levelled, so a bunker's lip
  // is not read as a slope to be planed away
  const drops = new Float32Array(nx * nz);
  const id = (s: Surface) => SURFACES.indexOf(s);

  for (let j = 0; j < nz; j++) {
    const z = j * cell;
    const cx = centerX(z);
    for (let i = 0; i < nx; i++) {
      const x = -widthM / 2 + i * cell;
      let h =
        1.2 * Math.sin(x * 0.05 + p1) * Math.cos(z * 0.03 + p2) +
        0.8 * Math.sin(z * 0.07 + p3) +
        0.4 * Math.sin((x + z) * 0.11 + p4);
      const dCup = Math.hypot(x - cup.x, z - cup.z);
      const dTee = Math.hypot(x - tee.x, z - tee.z);
      // 2026-09-13: a lower, wider plateau and the base terrain damped to a fifth on the green.
      // The original (0.7 m over 1.2 r, 60 % damping) left ~6 % slopes on a green whose friction
      // was below gravity's pull on them: from 4 m on one hole no aim within ±3° could hole a
      // putt at any power. Same seeds, same holes, a putting surface at 1–3 %.
      const gr = greenRadius * 1.6;
      h += 0.35 * Math.exp(-(dCup * dCup) / (2 * gr * gr)); // the green sits on a low plateau
      h = h * (1 - Math.exp(-(dCup * dCup) / (2 * greenRadius * greenRadius)) * 0.8); // and is flatter on top
      if (dTee < 7) h = h * Math.min(1, (dTee - 4) / 3 > 0 ? (dTee - 4) / 3 : 0) + 0.2; // a flat tee box

      let s: Surface;
      const d = Math.abs(x - cx);
      const half = fairwayHalf - (brief.hazards.includes('trees') && Math.sign(x - cx) === treeSide ? 5 : 0);
      if (Math.abs(x) > widthM / 2 - 2 || z < 0 || z > lengthM) s = 'oob';
      else if (dTee < 5) s = 'tee';
      else if (dCup < greenRadius) s = 'green';
      else if (d < half) s = 'fairway';
      else s = 'rough';
      // sand stops at the green's edge: a greenside bunker used to bite into the putting surface
      // and leave a 0.35 m step (a 9 % grade) inside it
      for (const b of bunkers) if (inside(b, x, z) && s !== 'tee' && dCup > greenRadius) { s = 'sand'; drops[j * nx + i] -= 0.35; }
      for (const p of ponds) if (inside(p, x, z) && s !== 'tee' && dCup > greenRadius) { s = 'water'; drops[j * nx + i] -= 0.9; }

      heights[j * nx + i] = h;
      surfaces[j * nx + i] = id(s);
    }
  }

  levelGreen(heights, nx, nz, cell, cup, greenRadius);
  for (let k = 0; k < heights.length; k++) heights[k] += drops[k];

  return { index, seed, brief, lengthM, widthM, cell, nx, nz, tee, cup, greenRadius, heights, surfaces, centerline };
}

/** The putting surface may not slope more than GREEN_MAX_GRADE anywhere inside the green, and the
 *  limit relaxes across a collar out to twice the radius so there is no lip. The damping above
 *  only reached the centre: at three quarters of the radius 40 % of the raw terrain was left, and
 *  the damping's own falloff added a slope proportional to the terrain's height, so 6–10 % greens
 *  survived it and an 8 m putt from the high side could not be holed at any power (QC round 2).
 *  This is the min-plus Lipschitz envelope: every cell becomes the lowest of (itself, any
 *  neighbour plus the allowed rise), swept until nothing moves. Peaks are planed down toward the
 *  local low; nothing is raised; the result is continuous and its grade is within the bound. */
export const GREEN_MAX_GRADE = 0.03;
export function levelGreen(heights: Float32Array, nx: number, nz: number, cell: number, cup: { x: number; z: number }, greenRadius: number): void {
  const widthM = (nx - 1) * cell;
  const bound = new Float32Array(nx * nz);
  for (let j = 0; j < nz; j++) {
    for (let i = 0; i < nx; i++) {
      const x = -widthM / 2 + i * cell, z = j * cell;
      const d = Math.hypot(x - cup.x, z - cup.z);
      bound[j * nx + i] = d < greenRadius ? GREEN_MAX_GRADE : d < greenRadius * 2 ? GREEN_MAX_GRADE + ((d - greenRadius) / greenRadius) * (0.3 - GREEN_MAX_GRADE) : 1;
    }
  }
  const diag = Math.SQRT2;
  const relax = (i: number, j: number, di: number, dj: number, dist: number): boolean => {
    const ni = i + di, nj = j + dj;
    if (ni < 0 || ni >= nx || nj < 0 || nj >= nz) return false;
    const k = j * nx + i, n = nj * nx + ni;
    const g = Math.min(bound[k], bound[n]);
    if (g >= 1) return false;
    const lim = heights[n] + g * dist * cell;
    if (heights[k] > lim + 1e-6) { heights[k] = lim; return true; }
    return false;
  };
  for (let pass = 0; pass < 8; pass++) {
    let moved = false;
    for (let j = 0; j < nz; j++) for (let i = 0; i < nx; i++) {
      if (relax(i, j, -1, 0, 1)) moved = true;
      if (relax(i, j, 0, -1, 1)) moved = true;
      if (relax(i, j, -1, -1, diag)) moved = true;
      if (relax(i, j, 1, -1, diag)) moved = true;
    }
    for (let j = nz - 1; j >= 0; j--) for (let i = nx - 1; i >= 0; i--) {
      if (relax(i, j, 1, 0, 1)) moved = true;
      if (relax(i, j, 0, 1, 1)) moved = true;
      if (relax(i, j, 1, 1, diag)) moved = true;
      if (relax(i, j, -1, 1, diag)) moved = true;
    }
    if (!moved) break;
  }
}

export function courseFromSpec(spec: CourseSpec): Hole[] {
  return spec.holes.map((b, i) => generateHole(spec.seed, i, b));
}

/** Bilinear height at (x, z); clamped to the grid at the edges. */
export function sampleHeight(h: Hole, x: number, z: number): number {
  const fx = (x + h.widthM / 2) / h.cell;
  const fz = z / h.cell;
  const i0 = Math.max(0, Math.min(h.nx - 2, Math.floor(fx)));
  const j0 = Math.max(0, Math.min(h.nz - 2, Math.floor(fz)));
  const tx = Math.max(0, Math.min(1, fx - i0));
  const tz = Math.max(0, Math.min(1, fz - j0));
  const a = h.heights[j0 * h.nx + i0], b = h.heights[j0 * h.nx + i0 + 1];
  const c = h.heights[(j0 + 1) * h.nx + i0], d = h.heights[(j0 + 1) * h.nx + i0 + 1];
  return (a * (1 - tx) + b * tx) * (1 - tz) + (c * (1 - tx) + d * tx) * tz;
}

/** Surface under (x, z): nearest cell; anything off the grid is out of bounds. */
export function sampleSurface(h: Hole, x: number, z: number): Surface {
  if (Math.abs(x) > h.widthM / 2 || z < 0 || z > h.lengthM) return 'oob';
  const i = Math.round((x + h.widthM / 2) / h.cell);
  const j = Math.round(z / h.cell);
  const ii = Math.max(0, Math.min(h.nx - 1, i)), jj = Math.max(0, Math.min(h.nz - 1, j));
  return SURFACES[h.surfaces[jj * h.nx + ii]];
}

/** Ground slope at (x, z), by central differences. */
export function gradient(h: Hole, x: number, z: number): { gx: number; gz: number } {
  const e = 0.5;
  return {
    gx: (sampleHeight(h, x + e, z) - sampleHeight(h, x - e, z)) / (2 * e),
    gz: (sampleHeight(h, x, z + e) - sampleHeight(h, x, z - e)) / (2 * e),
  };
}

export function holePar(h: Hole): number {
  return h.brief.par;
}
